import React, { useState, useEffect, useMemo, useRef } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import { AgGridReact } from 'ag-grid-react';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Select from "react-select";
import redirectURL from "../redirectURL";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaInfoCircle } from "react-icons/fa";
import { autoSizeAllColumns } from "../common/utils/agGridAutoSize.js";

import "./datepicker.css"

const todayIST = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const WarehouseTransferrableStock = () => {
  const gridApi = useRef(null);
  const gridColumnApi = useRef(null);
  const [loadshow, setLoadshow] = useState("show-n");
  const [overlayBlock, setOverlayBlock] = useState("show-n");
  const [overlyBlock, setOverlyBlock] = useState("show-n");
  const [show, setShow] = useState(false);
  const [basicTitle, setBasicTitle] = useState("");
  const [basicType, setBasicType] = useState("default");
  const [rowData, setRowData] = useState([]);
  
  const [currentDate, setCurrentDate] = useState("");
  const [warehouse, setWarehouse] = useState(null);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  
  // Slider state for Trigger
  const [isSliderOpen, setSliderOpen] = useState(false);
  const [sliderType, setSliderType] = useState(null);
  const [sliderCl, setSliderCl] = useState("");
  const [sliderTitle, setSliderTitle] = useState("");
  const [triggerDate, setTriggerDate] = useState(() => todayIST());
  const [isTriggering, setIsTriggering] = useState(false);

  // Custom Header Component with sorting and tooltip
  const CustomHeader = (props) => {
    const tooltip = props?.column?.getColDef()?.headerTooltip || "";
    const [showMenu, setShowMenu] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

    const onMenuClick = (event) => {
      props.showColumnMenu(event.currentTarget);
    };

    const onSortRequested = (order, event) => {
      props.setSort(order, event?.shiftKey);
    };

    const getSort = () => {
      return props.column.getSort();
    };

    const isSortable = props.column.getColDef().sortable;

    return (
      <div
        style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}
        onMouseEnter={() => setShowMenu(true)}
        onMouseLeave={() => setShowMenu(false)}
      >
        {/* Header Name - Clickable for sorting */}
        <span
          style={{
            cursor: isSortable ? "pointer" : "default",
            userSelect: "none"
          }}
          onClick={isSortable ? () => {
            const currentSort = getSort();
            let newSort = 'asc';
            if (currentSort === 'asc') newSort = 'desc';
            else if (currentSort === 'desc') newSort = null;
            onSortRequested(newSort, null);
          } : undefined}
        >
          {props.displayName}
        </span>

        {/* Sort indicator */}
        {isSortable && (
          <div style={{ display: "flex", flexDirection: "column", marginRight: 2 }}>
            <span
              style={{
                cursor: "pointer",
                fontSize: 10,
                opacity: getSort() === 'asc' ? 1 : 0.3,
                lineHeight: "8px"
              }}
              onClick={() => onSortRequested('asc', null)}
            >
              ↑
            </span>
            <span
              style={{
                cursor: "pointer",
                fontSize: 10,
                opacity: getSort() === 'desc' ? 1 : 0.3,
                lineHeight: "8px"
              }}
              onClick={() => onSortRequested('desc', null)}
            >
              ↓
            </span>
          </div>
        )}

        {/* Info Icon with tooltip */}
        <div
          style={{ position: "relative", display: "inline-block" }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <FaInfoCircle style={{ fontSize: 18, opacity: 0.7 }} />
          
          {/* Tooltip */}
          {showTooltip && tooltip && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "rgba(248, 244, 244, 0.8)",
                color: "white",
                padding: "8px 12px",
                borderRadius: "4px",
                fontSize: "12px",
                whiteSpace: "nowrap",
                zIndex: 1000,
                marginTop: "5px",
              }}
            >
              {tooltip}
            </div>
          )}
        </div>

        {/* Menu Button (☰) */}
        {props.enableMenu && (
          <span
            onClick={onMenuClick}
            style={{
              marginLeft: "5px",
              cursor: "pointer",
              fontSize: 14,
              opacity: showMenu ? 1 : 0,
              transition: "opacity 0.2s ease",
            }}
          >
            ☰
          </span>
        )}
      </div>
    );
  };

  // Column Definitions for Transferable Stock
  const columnDefs = [
    {
      headerName: "Destination Store Name",
      field: "destination_store_name",
      sortable: true,
      filter: true,
      minWidth: 200,
    },
    {
      headerName: "Destination Store ID",
      field: "destination_store_id",
      sortable: true,
      filter: true,
      minWidth: 150,
    },
    {
      headerName: "Destination Type",
      field: "destination_type",
      sortable: true,
      filter: true,
      minWidth: 150,
    },
    {
      headerName: "SKU Code",
      field: "sku_code",
      sortable: true,
      filter: true,
      minWidth: 150,
    },
    {
      headerName: "SKU Name",
      field: "sku_name",
      sortable: true,
      filter: true,
      minWidth: 250,
    },
    {
      headerName: "Closing Balance",
      field: "cls_bal",
      sortable: true,
      minWidth: 150,
      valueGetter: (params) => params.data ? params.data.cls_bal : null,
    },
    {
      headerName: "Batch No",
      field: "batch_no",
      sortable: true,
      filter: true,
      minWidth: 150,
    },
    {
      headerName: "M Date",
      field: "m_date",
      sortable: true,
      minWidth: 170,
      valueGetter: (params) =>
        params.data && params.data.m_date
          ? new Date(params.data.m_date).toLocaleDateString()
          : "",
    },
    {
      headerName: "E Date",
      field: "e_date",
      sortable: true,
      minWidth: 150,
      valueGetter: (params) =>
        params.data && params.data.e_date
          ? new Date(params.data.e_date).toLocaleDateString()
          : "",
    },
    {
      headerName: "Total Shelf Life(Days)",
      field: "total_shelf_life",
      sortable: true,
      minWidth: 150,
      valueGetter: (params) => (params.data ? params.data.total_shelf_life : null),
    },
    {
      headerName: "Non-Fresh Date",
      field: "non_fresh_date",
      sortable: true,
      minWidth: 150,
      headerTooltip: "M Date + (Total ShelfLife * 0.3)",
      headerComponent: CustomHeader,
      valueGetter: (params) =>
        params.data && params.data.non_fresh_date
          ? new Date(params.data.non_fresh_date).toLocaleDateString()
          : "",
    },
    {
      headerName: "Non-Invoicable Date",
      field: "non_invoicable_date",
      sortable: true,
      minWidth: 180,
      headerTooltip: "M Date + (Total ShelfLife * 0.5)",
      headerComponent: CustomHeader,
      valueGetter: (params) =>
        params.data && params.data.non_invoicable_date
          ? new Date(params.data.non_invoicable_date).toLocaleDateString()
          : "",
    },
    {
      headerName: "Target Date",
      field: "target_date",
      sortable: true,
      minWidth: 150,
      headerTooltip: "Non Fresh Date - (Total ShelfLife * 0.05)",
      headerComponent: CustomHeader,
      hide: true,
      valueGetter: (params) =>
        params.data && params.data.target_date
          ? new Date(params.data.target_date).toLocaleDateString()
          : "",
    },
    {
      headerName: "Decision",
      field: "decision",
      sortable: true,
      filter: true,
      minWidth: 200,
      headerTooltip: "Transferable if Today <= Non Fresh date",
      headerComponent: CustomHeader,
      valueGetter: (params) => (params.data ? params.data.decision : ""),
    },
    {
      headerName: "Date",
      field: "date",
      sortable: true,
      width: 120,
      hide: true,
      valueGetter: (params) =>
        params.data && params.data.date
          ? new Date(params.data.date).toLocaleDateString()
          : "",
    },
    {
      headerName: "Inserted Date",
      field: "inserted_date",
      sortable: true,
      width: 150,
      hide: true,
      valueGetter: (params) =>
        params.data && params.data.inserted_date
          ? new Date(params.data.inserted_date).toLocaleDateString()
          : "",
    },
    {
      headerName: "Updated Date",
      field: "updated_date",
      sortable: true,
      width: 150,
      hide: true,
      valueGetter: (params) =>
        params.data && params.data.updated_date
          ? new Date(params.data.updated_date).toLocaleDateString()
          : "",
    },
  ];

  const sideBar = useMemo(() => ({
    toolPanels: [
      {
        id: "columns",
        labelDefault: "Columns",
        labelKey: "columns",
        iconKey: "columns",
        toolPanel: "agColumnsToolPanel",
      },
      {
        id: "filters",
        labelDefault: "Filters",
        labelKey: "filters",
        iconKey: "filter",
        toolPanel: "agFiltersToolPanel",
      },
    ]
  }), []);

  const statusPanel = {
    statusPanels: [
      {
        statusPanel: "agTotalAndFilteredRowCountComponent",
        align: "left"
      },
      {
        statusPanel: "agTotalRowCountComponent",
        align: "center"
      },
      { statusPanel: "agFilteredRowCountComponent" },
      { statusPanel: "agSelectedRowCountComponent" },
      { statusPanel: "agAggregationComponent" }
    ]
  };

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100,
  };

  // Fetch warehouses for a specific date
  const getWarehousesForDate = async (date) => {
    try {
      const response = await redirectURL.post("indentation/getWarehousesForTransfer", {
        date: date
      });
      
      if (response.status === 200 && response.data.warehouses) {
        const warehouseOptions = response.data.warehouses.map(wh => ({ 
          value: wh, 
          label: wh 
        }));
        warehouseOptions.unshift({ label: "ALL", value: "" });
        setWarehouseOptions(warehouseOptions);
        setWarehouse(warehouseOptions[0]);
      }
    } catch (error) {
      console.error("Failed to fetch warehouses for date:", error);
      // Reset to empty options if error
      const emptyOptions = [{ label: "ALL", value: "" }];
      setWarehouseOptions(emptyOptions);
      setWarehouse(emptyOptions[0]);
    }
  };

  // Fetch initial data
  const getMainData = async () => {
    setLoadshow("show-m");
    setOverlayBlock("show-m");
    
    try {
      const response = await redirectURL.post("indentation/getTransferrableStock", {});
      
      if (response.status === 200) {
        if (response.data.data && response.data.data.length > 0) {
          const { date, warehouses, data } = response.data;
          
          const formattedDate = date ? new Date(date).toISOString().split('T')[0] : '';
          
          // Warehouse options
          const warehouseOptions = warehouses.map(wh => ({ 
            value: wh, 
            label: wh 
          }));
          warehouseOptions.unshift({ label: "ALL", value: "" });
          
          setWarehouseOptions(warehouseOptions);
          setWarehouse(warehouseOptions[0]);
          setRowData(data);
          setCurrentDate(formattedDate);
          
          // Get available dates
          await getAvailableDates();
        } else {
          setBasicTitle("No transferable stock data found");
          setBasicType("info");
          setShow(true);
        }
      }
    } catch (error) {
      setBasicTitle("Failed to fetch transferable stock data");
      setBasicType("error");
      setShow(true);
    } finally {
      setLoadshow("show-n");
      setOverlayBlock("show-n");
    }
  };

  // Get available dates
  const getAvailableDates = async () => {
    try {
      const response = await redirectURL.post("indentation/getTransferStockDates", {});
      if (response.status === 200 && response.data.dates) {
        // Convert to Date objects for DatePicker
        const dates = response.data.dates.map(dateStr => {
          try {
            return new Date(dateStr);
          } catch (e) {
            return null;
          }
        }).filter(date => date !== null);
        setAvailableDates(dates);
      }
    } catch (error) {
      console.error("Failed to fetch available dates:", error);
    }
  };

  // Handle date change
  const handleDateChange = async (date) => {
    if (date) {
      const formattedDate = date.toISOString().split('T')[0];
      setCurrentDate(formattedDate);
      
      // Clear warehouse selection
      setWarehouse({ label: "ALL", value: "" });
      
      // Fetch warehouses for the selected date
      await getWarehousesForDate(formattedDate);
    } else {
      setCurrentDate("");
      // Reset warehouse options when date is cleared
      const emptyOptions = [{ label: "ALL", value: "" }];
      setWarehouseOptions(emptyOptions);
      setWarehouse(emptyOptions[0]);
    }
  };

  // Apply filters
  const applyFilters = async () => {
    setLoadshow("show-m");
    setOverlayBlock("show-m");

    const params = {};
    
    if (currentDate) {
      params.date = currentDate;
    }
    
    if (warehouse && warehouse.value) {
      params.destination_store_name = warehouse.value;
    }

    try {
      const response = await redirectURL.post(
        "indentation/getTransferDataByFilter",
        params
      );
      const data = response.data.data;
      setRowData(data);
    } catch (error) {
      setBasicTitle("Error fetching filtered data");
      setBasicType("error");
      setShow(true);
    } finally {
      setLoadshow("show-n");
      setOverlayBlock("show-n");
    }
  };

  // Reset filters
  const resetFilters = async () => {
    setLoadshow("show-m");
    setOverlayBlock("show-m");
    await getMainData();
    setLoadshow("show-n");
    setOverlayBlock("show-n");
  };

  // Close alert
  const closeAlert = () => {
    setShow(false);
  };

  // Initialize on component mount
  useEffect(() => {
    getMainData();
  }, []);

  const onGridReady = (params) => {
    gridApi.current = params.api;
    gridColumnApi.current = params.columnApi;
  };

  // Slider functions
  const hideOverlySlideBlock = () => {
    setOverlyBlock("show-n");
    setLoadshow("show-n");
    setSliderOpen(false);
    setTriggerDate(todayIST());
  };

  const onClickingSliderCloseIcon = () => {
    setOverlyBlock("show-n");
    setSliderOpen(false);
    setTriggerDate(todayIST());
  };

  const openTriggerSlider = () => {
    setSliderType(1);
    setSliderTitle("Generate Transferable Stock");
    setSliderCl("transfer-stock-slider");
    setOverlyBlock("show-m");
    setSliderOpen(true);
  };

  const onSaveTrigger = async (e) => {
    e.preventDefault();
    if (!triggerDate) {
      setShow(true);
      setBasicType("info");
      setBasicTitle("Please select a date");
      return;
    }

    try {
      setIsTriggering(true);
      setLoadshow("show-m");
      setOverlayBlock("show-m");

      const response = await redirectURL.post(
        "indentation/runTransferStockGeneration",
        { trigger_date: triggerDate }
      );

      const res = response?.data?.pyResponse;

      if (res?.status === "Success") {
        setLoadshow("show-n");
        setOverlayBlock("show-n");
        setShow(true);
        setBasicType("success");
        setBasicTitle(res.message);
        setSliderOpen(false);
        setOverlyBlock("show-n");
        setTriggerDate(todayIST());
        
        const userData = {
          email: localStorage.getItem("email"),
          user_code: localStorage.getItem("user_code"),
          action: "Generate Transferable Stock",
          status: res.status,
          path: window.location.pathname
        };
        triggerUserAction(userData);

        // Refresh the data after successful trigger
        await getMainData();
      } 
      else {
        setLoadshow("show-n");
        setOverlayBlock("show-n");
        setShow(true);
        setBasicType("info");
        setBasicTitle(res?.message || "Failed to Generate Transferable Stock");

        // Optional audit for failures
        triggerUserAction({
          email: localStorage.getItem("email"),
          user_code: localStorage.getItem("user_code"),
          action: "Generate Transferable Stock",
          status: res?.status || "Failure",
          path: window.location.pathname
        });
      }

    } catch (error) {
      console.error("Error running Transfer Stock Generation:", error);
      setLoadshow("show-n");
      setOverlayBlock("show-n");
      setShow(true);
      setBasicType("error");
      setBasicTitle(
        error?.response?.data?.message || "Failed to Generate Transferable Stock. Please try again later."
      );
      
      const userData = {
        email: localStorage.getItem("email"),
        user_code: localStorage.getItem("user_code"),
        action: "Generate Transferable Stock",
        status: "Failed",
        path: window.location.pathname
      };
      triggerUserAction(userData);
    } finally {
      setIsTriggering(false);
    }
  };

  const triggerUserAction = (trigerdata) => {
    redirectURL.post('inventory/triggerUserAction', trigerdata);
  };
  
  // Select styles
  const selectStyles = {
    control: (base) => ({
      ...base,
      padding: '1px',
      borderRadius: '5px',
      border: '1px solid #d9d9d9',
      minHeight: '38px'
    }),
    singleValue: (base) => ({
      ...base,
      fontWeight: 'normal',
      color: '#333',
      fontSize: '14px'
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '14px',
      fontWeight: state.isSelected ? 'normal' : 'normal',
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: '14px',
      color: '#666',
      fontWeight: 'normal'
    })
  };

  return (
    <>
      <div className="container-fluid mt-3">
        <div style={{ zIndex: "9999999" }}>
          <SweetAlert
            show={show}
            type={basicType}
            title={basicTitle}
            onConfirm={closeAlert}
            style={{ fontSize: "9px" }}
          />
        </div>

        <div className="row" style={{ fontSize: "0.9rem", fontFamily: "Arial, sans-serif" }}>
          {/* <div className="">
            <h1 className="sku-header-title">Warehouse Transferable Stock</h1>
          </div> */}
          <form className="col-sm-12  mt-2 mb-4" style={{ display: "flex", alignItems: "center" }}>
            <div className="col-sm-12 row">
                
              {/* Date Filter */}
              <div className="col-sm-2 mb-1" style={{ display: "flex", flexDirection: "column" }}>
                <label style={{ fontSize: "14px", fontWeight: "400", marginBottom: "5px" }}>
                  Date:
                </label>
                <DatePicker
                  selected={currentDate ? new Date(currentDate) : null}
                  onChange={handleDateChange}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select date"
                  className="form-control"
                  highlightDates={availableDates}
                  maxDate={new Date()}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "5px",
                    border: "1px solid #d9d9d9",
                    width: "100%",
                    cursor: "pointer",
                    fontSize: "14px",
                    height: "38px"
                  }}
                />
              </div>

              {/* Destination Warehouse Filter */}
              <div className="col-sm-2 mb-1" style={{ display: 'flex', flexDirection: 'column' }}>
                <label htmlFor="warehouse" style={{ fontSize: '14px', fontWeight: '400', marginBottom: '5px' }}>
                  Destination Warehouse:
                </label>
                <Select
                  id="warehouse"
                  value={warehouse}
                  onChange={setWarehouse}
                  options={warehouseOptions}
                  styles={selectStyles}
                  placeholder="Select warehouse"
                  isDisabled={!currentDate}
                />
              </div>

              {/* Action Buttons */}
              <div className="col-sm-4 mt-4" style={{ 
                display: "flex", 
                gap: "10px",
                paddingLeft: "20px"
              }}>
                <button
                  type="button"
                  onClick={applyFilters}
                  style={{
                    backgroundColor: "#93385D",
                    color: "#fff",
                    borderRadius: "5px",
                    padding: "8px 25px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    height: "38px",
                    minWidth: "100px"
                  }}
                >
                  Apply
                </button>
                <button
                  onClick={resetFilters}
                  type="button"
                  style={{
                    backgroundColor: "#fff",
                    color: "#000",
                    borderRadius: "5px",
                    padding: "8px 25px",
                    border: "1px solid #d9d9d9",
                    cursor: "pointer",
                    fontSize: "14px",
                    height: "38px",
                    minWidth: "100px"
                  }}
                >
                  Reset
                </button>
              </div>

              {/* Trigger Button */}
              <div className="col-sm-4 mt-4" style={{ 
                display: "flex", 
                justifyContent: "flex-end",
                paddingRight: "20px"
              }}>
                <button
                  type="button"
                  onClick={openTriggerSlider}
                  style={{
                    backgroundColor: "#93385D",
                    color: "#fff",
                    borderRadius: "5px",
                    padding: "8px 25px",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "14px",
                    height: "38px",
                    minWidth: "220px"
                  }}
                >
                  Generate Transferable Stock
                </button>
              </div>
            </div>
          </form>

          {/* AG Grid Table */}
          <div className="col-sm-12 p-0">
            <div className="ag-theme-alpine" style={{ height: 600, width: "100%" }}>
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                domLayout="normal"
                sideBar={sideBar}
                statusBar={statusPanel}
                // pagination={true}
                // paginationPageSize={100}
                enableRangeSelection={true}
                onGridReady={onGridReady}
                onFirstDataRendered={() => {
                  autoSizeAllColumns(gridApi.current, gridColumnApi.current);
                }}
                onColumnVisible={() =>
                  autoSizeAllColumns(gridApi.current, gridColumnApi.current)
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Slider for Trigger Transfer Stock Generation */}
      <div
        className={`stock-status-slider ${isSliderOpen ? "open" : ""} ${sliderCl}`}
        style={{ width: "25%" }}
      >
        <div className="slider-header-container d-flex justify-content-between">
          <p className="m-0p">{sliderTitle}</p>
          <p
            className="m-0p"
            style={{ cursor: "pointer" }}
            onClick={onClickingSliderCloseIcon}
          >
            X&nbsp;&nbsp;&nbsp;
          </p>
        </div>
        {sliderType === 1 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflowY: "auto",
              padding: 5,
            }}
          >
            <form
              style={{ flexGrow: 1, overflowY: "auto", padding: "10px" }}
              onSubmit={onSaveTrigger}
            >
              <div className="mb-1" style={{ display: "flex", flexDirection: "column" }}>
                <label
                  htmlFor="triggerDate"
                  style={{ fontSize: "14px", fontWeight: "400", marginBottom: "5px", cursor: "pointer" }}
                >
                  Date:
                </label>
                <DatePicker
                  selected={triggerDate ? new Date(triggerDate) : null}
                  onChange={(date) => setTriggerDate(date ? date.toISOString().split('T')[0] : '')}
                  dateFormat="yyyy-MM-dd"
                  className="form-control"
                  placeholderText="Select Date"
                  minDate={new Date("2025-09-01")}
                  maxDate={new Date()}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "5px",
                    border: "1px solid #d9d9d9",
                    width: "100%",
                    marginBottom: "15px",
                    cursor: "pointer",
                    fontSize: "14px",
                    height: "38px"
                  }}
                  required
                />
              </div>

              <div style={{ textAlign: "right", marginTop: "20px" }}>
                <button
                  className="btn"
                  style={{
                    backgroundColor: "#93385D",
                    color: "#fff",
                    fontSize: "14px",
                    padding: "8px 25px",
                    height: "38px",
                    minWidth: "220px"
                  }}
                  type="submit"
                  disabled={isTriggering}
                >
                  {isTriggering ? "Processing..." : "Generate Transfer Stock"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Loader and Overlay */}
      <div className={`loader ${loadshow}`} style={{ zIndex: "1002" }}></div>
      <div className={`overlay-block ${overlayBlock}`}></div>
      <div
        className={"overly-block " + overlyBlock}
        onClick={hideOverlySlideBlock}
      ></div>
    </>
  );
};

export default WarehouseTransferrableStock;